import {
  Button,
  Link,
  Paper,
  PaperProps,
  styled,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  FacebookIcon,
  FacebookShareButton,
  LinkedinIcon,
  LinkedinShareButton,
  TwitterIcon,
  TwitterShareButton,
} from "react-share";

import { PaddedPaper } from "components/surfaces/PaddedPaper";
import { PdfIcon } from "components/icons/PdfIcon";
import { CloneProject } from "./CloneProject";
import { NavLink } from "components/navigation/NavLink";
import CreateNewStudyArea from "components/draw/basePolygon/CreateNewStudyArea";
import { JoyrideActivator } from "components/joyride/JoyrideActivator";
import { useSearchParams } from "react-router-dom";
import { ShareLinkTextField } from "components/result/ShareLinkTextField";

interface Props extends PaperProps {
  uuid: string;
  readOnlyUuid: string;
  isReadOnly: boolean;
}

export function CostBenefitsShare(props: Props) {
  const { uuid, isReadOnly, readOnlyUuid, ...paperProps } = props;
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const link = window.location.href;
  const readOnlyLink = `${
    window.location.origin
  }/${readOnlyUuid}/measures?${params.toString()}`;

  return (
    <StyledPaper {...paperProps} data-tour="step-5">
      <Typography variant="h2" component="h2" marginBottom={2}>
        {t("label.shareAndEdit")}
      </Typography>
      <Paper className="content">
        <JoyrideActivator />
        <div className="action-buttons">
          <Button
            variant="green-contained"
            component={NavLink}
            to={`/${uuid}/measures`}
          >
            {t("label.improveScore")}
          </Button>
          <CloneProject uuid={uuid} />
          <CreateNewStudyArea />
        </div>
        <ShareLinkTextField link={link} label={t("label.linkToProject")} />
        {!isReadOnly && (
          <ShareLinkTextField
            link={readOnlyLink}
            label={t("label.linkToReadOnlyMode")}
          />
        )}
        <Link href={`/${uuid}/print`} target="_blank" className="icon-link">
          <PdfIcon fontSize="small" />
          {t("label.downloadAPDFRapport")}
        </Link>
        <Typography variant="subtitle1">
          {t("label.shareTheseResults")}
        </Typography>
        <div className="socials">
          <TwitterShareButton url={readOnlyLink}>
            <TwitterIcon size={32} />
          </TwitterShareButton>
          <FacebookShareButton url={readOnlyLink}>
            <FacebookIcon size={32} />
          </FacebookShareButton>
          <LinkedinShareButton url={readOnlyLink}>
            <LinkedinIcon size={32} />
          </LinkedinShareButton>
        </div>
      </Paper>
    </StyledPaper>
  );
}

const StyledPaper = styled(PaddedPaper)`
  padding: 0;
  .content {
    height: fit-content;
    display: flex;
    border: 1px solid ${({ theme }) => theme.palette.grey[100]};
    flex-direction: column;
    width: 100%;
    padding: ${({ theme }) => theme.spacing(2)};
    margin-left: auto;
    gap: ${({ theme }) => theme.spacing(2)};
    .socials {
      display: flex;
      gap: ${({ theme }) => theme.spacing(1)};
    }
    .icon-link {
      display: inline-flex;
      gap: ${({ theme }) => theme.spacing(1)};
    }

    .MuiButton-green-contained {
      width: fit-content;
    }

    .action-buttons {
      display: flex;
      gap: ${({ theme }) => theme.spacing(1)};
      flex-wrap: wrap;
    }
  }
`;
